import { getFocusableTreeWalker, useFocusRing } from '@react-aria/focus';
import { isFocusVisible } from '@react-aria/interactions';
import { mergeProps, useEffectEvent as useRACEffectEvent, useLayoutEffect } from '@react-aria/utils';
import { type KeyboardEventHandler, type FocusEventHandler, type RefObject, useEffect } from 'react';
import type { FocusableElement, NativeElementProps } from '../../types.js';
import { useTableFocus } from '../contexts/focus.js';
import { useTableRowState } from '../contexts/row-state.js';
import { useTableState } from '../contexts/table-state.js';
import { focusTableChild } from '../utils/focus-table-child.js';

export interface UseTableCell {
  cellProps: NativeElementProps<HTMLTableCellElement>;
}

export function useTableCell(
  ref: RefObject<HTMLTableCellElement | null>,
  rowKey: string,
  columnKey: string,
): UseTableCell {
  const tableFocus = useTableFocus();
  const { pendingRowSelectionState, setPendingRowSelectionState } = useTableState();
  const { onSelectionToggle } = useTableRowState();

  const { focusProps, isFocusVisible: isCellFocusVisible } = useFocusRing();

  const isTabbable = rowKey === tableFocus.focusedRowKey && columnKey === tableFocus.focusedColumnKey;

  // Handles focusing the cell.
  // If there is a focusable child, it is focused, otherwise the cell itself is focused.
  const focusCell = () => {
    if (!ref.current) {
      return;
    }

    // If there's a pending selection state, apply it to this cell's row.
    // If this cell is a header cell, `onSelectionToggle` will be undefined, but we still want to clear the pending selection state.
    if (pendingRowSelectionState !== undefined) {
      onSelectionToggle?.(pendingRowSelectionState);
      setPendingRowSelectionState(undefined);
    }

    // If focus is already on a focusable child within the cell, early return so we don't shift focus
    if (ref.current.contains(document.activeElement) && ref.current !== document.activeElement) {
      return;
    }

    const treeWalker = getFocusableTreeWalker(ref.current);
    const focusable = tableFocus.childFocusStrategy === 'last' ? treeWalker.lastChild() : treeWalker.firstChild();

    if (focusable) {
      // Cell has focusable children, focus the child
      focusTableChild(focusable as FocusableElement);
    } else if (!ref.current.contains(document.activeElement)) {
      // Cell has no focusable children and current focus is outside of the cell, focus the cell itself
      focusTableChild(ref.current);
    } else if (ref.current === document.activeElement && !isTabbable) {
      // Focus is on the current cell, but table focus is elsewhere, update table focus state
      tableFocus.setFocus(rowKey, columnKey);
    }
  };

  // Focus the cell DOM node when table focus moves to this cell
  useLayoutEffect(() => {
    if (isTabbable && !ref.current!.contains(document.activeElement)) {
      focusCell();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, isTabbable]);

  const resetIfFocused = useRACEffectEvent(() => {
    if (isTabbable) {
      tableFocus.setFocus('', '');
    }
  });

  // Reset table focus when the focused cell unmounts
  useEffect(
    () => () => {
      resetIfFocused();
    },
    [resetIfFocused],
  );

  const onFocus: FocusEventHandler = (e) => {
    // Ignore focus events bubbling through portals
    if (!e.currentTarget.contains(e.target)) {
      return;
    }

    // If cell is already focused, return early.
    // However, if focus happens as a result of a press, we need to re-run the child-focusing logic,
    // otherwise `useFocusRing` will force the cell itself to become focused.
    if (e.currentTarget.contains(e.relatedTarget) && isFocusVisible()) {
      return;
    }

    // There are 3 scenarios for why a cell might receive focus:
    // - focus happens through a press, we need to update table focus state to the current cell
    // - focus happens through arrow key navigation within the table,
    //   we do nothing as table focus state would have already been updated earlier
    // - focus happens through tabbing into the table from outside,
    //   we do nothing because we want to restore focus to the previously focused cell,
    //   and overriding table focus state would make that impossible
    if (!isFocusVisible()) {
      tableFocus.setFocus(rowKey, columnKey);
    }

    if (e.target === ref.current) {
      // If the cell itself receives focus, wait a frame so that focus finishes propagating
      // up to the tree, and move focus to a focusable child if possible.
      requestAnimationFrame(() => {
        if (document.activeElement === ref.current) {
          focusCell();
        }
      });
    }
  };

  // Handles keyboard navigation between focusable children within the cell.
  const onKeyDownCapture: KeyboardEventHandler = (e) => {
    // Ignore keyboard events bubbling through portals
    if (!e.currentTarget.contains(e.target as Element) || !ref.current || !document.activeElement) {
      return;
    }

    const walker = getFocusableTreeWalker(ref.current);
    walker.currentNode = document.activeElement;

    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      // Move to the previous/next focusable element within the cell
      const childElementToFocus = e.key === 'ArrowLeft' ? walker.previousNode() : walker.nextNode();

      if (childElementToFocus && childElementToFocus !== ref.current) {
        focusTableChild(childElementToFocus as FocusableElement);
      } else {
        // If there is no previous/next focusable element within the current cell, focus should move to the previous/next cell.
        // This is handled by `useTable`, so we need to let the event bubble up to it.
        // But we also don't want this event to reach children of the current cell, as arrow keys should be used exclusively for table navigation.
        // Solution: re-dispatch the event from a parent element of the current cell.
        ref.current.parentElement!.dispatchEvent(new KeyboardEvent(e.nativeEvent.type, e.nativeEvent));
      }
    } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      // Move to the previous/next cell in the current column.
      // This is handled by `useTable`, so we need to let the event bubble up to it.
      // But we also don't want this event to reach children of the current cell, as arrow keys should be used exclusively for table navigation.
      // Solution: re-dispatch the event from a parent element of the current cell.
      ref.current.parentElement?.dispatchEvent(new KeyboardEvent(e.nativeEvent.type, e.nativeEvent));
    } else {
      // if we didn't handle anything, return early so we don't stop propagation and preventDefault
      return;
    }

    e.preventDefault();
    e.stopPropagation();
  };

  const customProps: NativeElementProps<HTMLTableCellElement> = {
    tabIndex: isTabbable ? 0 : -1,
    onFocus,
    onKeyDownCapture,
    'data-cell-row-key': rowKey,
    'data-cell-column-key': columnKey,
    'data-focus-visible': isCellFocusVisible || undefined,
  };

  return {
    cellProps: mergeProps(focusProps, customProps),
  };
}
